/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { BaseComponent } from '../../index';
import { ChangeDetectorRef, Component } from '@angular/core';
import { DrawerService, LogService, NotifyService } from '../../../services';
import { ErrorResponseModel, NotificationModel } from '../../../models';
import { NavigationEnd, Router } from '@angular/router';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-notification-toast-wrapper',
    styleUrls: ['./notificationToastWrapper.component.scss'],
    templateUrl: './notificationToastWrapper.component.html',
    animations: [
        trigger('inOut', [
            state('in',
                style({
                    right: 0,
                })
            ),
            state('out',
                style({
                    right: -450,
                })
            ),
            transition('in => out',
                animate('{{animation}}ms ease-out'), { params: { animation: 0 } }),
            transition('out => in',
                animate('{{animation}}ms ease-out'), { params: { animation: 0 } })
        ])]
})

export class NotificationToastWrapperComponent extends BaseComponent {
    /**
     * Set the duration of the "in" / "out" animation transition in ms
     */
    public animation: number = 350;

    /**
     * Group the notifications into object types.
     * Note the order of these groups is in the order they display on the page.
     */
    public grouping: any = [
        { success: [] },
        { error: [] },
        { warning: [] },
        { info: [] },
        { debug: [] }
    ];

    /**
     * Add active ids to array for animation
     */
    public activeNotifications: any[] = [];

    /**
     * Flag to stop animations occurring on existing notifications after routing.
     */
    public firstLoad: boolean = true;

    /**
     * Stores if we are on notifications page
     */
    public isNotificationsPage: boolean = false;

    /**
     * Hide the notification wrapper to stop issues with empty wrappers and z-index with action dots.
     */
    public hideWrapper: boolean = false;

    /**
     * State of the drawer to move the toast wrapper to correct position in the view when the drawer is open.
     */
    public drawerState: boolean = false;

    /**
     * Stored notification to be added.
     * @private
     */
    private addNotificationQueue: NotificationModel[] = [];

    /**
     * Stored notification to be removed.
     * @private
     */
    private removeNotificationQueue: NotificationModel[] = [];

    /**
     * Remove state.
     * @private
     */
    private removeActive: boolean = false;

    /**
     * Gets notifications class name from the level.
     * @param level
     * @private
     */
    private static getLevelClassName(level: string | number): string {
        return ({
            5: 'debug',
            4: 'info',
            3: 'success',
            2: 'warning',
            1: 'error'
        })[level];
    }

    constructor(
        private cdr: ChangeDetectorRef,
        private drawerService: DrawerService,
        private logService: LogService,
        private notifyService: NotifyService,
        private router: Router
    ) {
        super();

        this.setUpNotificationSubscribers();
        this.setUpDrawerSubscriber();

        // Subscribe to page changes to decide whether to show the notifications
        this.router.events.subscribe({
            next: (event: any): void => {
                if (event instanceof NavigationEnd) {
                    this.isNotificationsPage = this.router.url.includes('/notifications');
                    if (!this.isNotificationsPage) {
                        this.firstLoad = false;
                    }
                }
            }
        });
    }

    /**
     * Get first property from object
     * @param object
     */
    public getFirstPropertyFromObject(object: any): NotificationModel[] {
        return object[Object.keys(object)[0]];
    }

    /**
     * Get group length
     * @param group
     */
    public getGroupLength(group: any): number {
        return this.getFirstPropertyFromObject(group).length;
    }

    /**
     * Get the margin top form the group
     * @param group
     */
    public getGroupMarginTop(group: any): number {
        const notifications: NotificationModel[] = this.getFirstPropertyFromObject(group);
        if (notifications?.length > 1) {
            return -45;
        }
    }

    /**
     * Subscriber to the notify service to get the latest notifications.
     * @private
     */
    private setUpNotificationSubscribers(): void {
        this.notifyService.addToToasterEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (notification: NotificationModel): void => {
                    const match: boolean = this.activeNotifications.some(item => item.id === notification.id);
                    if (
                        !match && !this.isWizardNotification(notification) &&
                        !notification.hidden && notification.id !== 'licenceNotification' &&
                        !this.firstLoad
                    ) {
                        this.addNotificationQueue.push(notification);
                        this.addNotification();
                    }
                },
                error: (error) => {
                    this.logService.warn('NotificationWrapperComponent:addToToasterEmitter ', error.message);
                }
            });

        this.notifyService.removeFromToasterEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (notification: NotificationModel): void => {
                    this.removeNotification(notification);
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.warn('NotificationWrapperComponent:removeFromToasterEmitter ', error.message);
                }
            });

        this.notifyService.clearToasterEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (state: boolean): void => {
                    if (state) {
                        this.clearAllToasters();
                    }
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.warn('NotificationWrapperComponent:clearToasterEmitter ', error.message);
                }
            });
    }

    /**
     * Add notification.
     * @private
     */
    private addNotification(): void {
        if (this.removeActive && this.addNotificationQueue.length > 1) {
            const waitTimer: any = setTimeout((): void => {
                this.processAdd(this.addNotificationQueue[0]);
                clearTimeout(waitTimer);
            }, this.animation);
        } else {
            this.processAdd(this.addNotificationQueue[0]);
        }
    }

    /**
     * Process add.
     * @param notification
     * @private
     */
    private processAdd(notification: NotificationModel): void {
        this.addNotificationToGroup(notification, true);
        // Kick change detector so new notification in group shows with animation.
        this.cdr.detectChanges();
        this.activeNotifications.push(notification);
        this.processQueue();
    }

    /**
     * Process queue.
     * @private
     */
    private processQueue(): void {
        this.addNotificationQueue.splice(0);
        if (this.addNotificationQueue.length) {
            this.addNotification();
        }
    }

    /**
     * Push notifications into groups.
     * @param notification
     * @param addCanAnimate
     * @private
     */
    private addNotificationToGroup(notification: NotificationModel, addCanAnimate?: boolean): void {
        const { groupIndex, level } = this.getGroupPath(notification);
        notification['canAnimate'] = addCanAnimate;
        this.grouping[groupIndex][level].push(notification);
    }

    /**
     * Remove notification.
     * @param notification
     * @private
     */
    private removeNotification(notification: NotificationModel): void {
        this.removeActive = true;
        this.removeNotificationQueue.push(notification);
        this.removeFromActiveArray(notification);
    }

    /**
     * Remove notification from active array.
     * @param notification
     * @private
     */
    private removeFromActiveArray(notification: NotificationModel): void {
        const activeIndex: number = this.activeNotifications.findIndex(item => item.id === notification.id);
        if (activeIndex !== -1) {
            this.activeNotifications.splice(activeIndex, 1);
        }
    }

    /**
     * At the end of the animation, action the notification.
     * @param $event
     * @param notification
     */
    public endOfAnimation($event: any, notification: NotificationModel): void {
        const deleteIndex: number = this.removeNotificationQueue.findIndex(
            (item: NotificationModel) => item.id === notification.id && item.level === notification.level
        );

        if (deleteIndex !== -1 && $event.toState === 'out') {
            this.removeNotificationFromGroup(notification);
            this.removeNotificationQueue.splice(deleteIndex, 1);
            this.removeActive = false;
        }
    }

    /**
     * Remove notification from component group.
     * @param notification
     * @private
     */
    private removeNotificationFromGroup(notification: NotificationModel): void {
        const { groupIndex, level } = this.getGroupPath(notification);
        const selectedGroup = this.grouping[groupIndex][level];
        const deleteIndex = selectedGroup.findIndex((item: NotificationModel): boolean => item.id === notification.id);
        if (deleteIndex !== -1) {
            selectedGroup.splice(deleteIndex, 1);
        }
    }

    /**
     * Check to see if the notification is for a modal and if there is an active modal.
     * @param notification
     * @private
     */
    private isWizardNotification(notification: NotificationModel): boolean {
        return notification.id === 'wizardError';
    }

    /**
     * Get the group path for a given notification.
     * @param notification
     * @private
     */
    private getGroupPath(notification: NotificationModel): any {
        const level: string = NotificationToastWrapperComponent.getLevelClassName(notification.level);
        const groupIndex = this.grouping.findIndex((item: NotificationModel) => item[level]);

        return { groupIndex, level };
    }

    /**
     * Clear all toaster notifications.
     * @private
     */
    private clearAllToasters(): void {
        this.addNotificationQueue = [];
        this.activeNotifications = [];
        this.removeNotificationQueue = [];
        this.grouping = [
            { success: [] },
            { error: [] },
            { warning: [] },
            { info: [] },
            { debug: [] }
        ];
    }

    /**
     * Subscribe to drawer state.
     * @private
     */
    private setUpDrawerSubscriber(): void {
        this.drawerService.drawerEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (state: boolean): void => {
                    this.drawerState = state;
                }
            });
    }
}
